import { motion } from "framer-motion";
import { PenTool, Smartphone, Globe, MonitorSmartphone, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/content";

const SERVICES = [
    {
        icon: PenTool,
        title: "UI/UX Design",
        accent: "from-violet-500 to-violet-700",
        glow: "shadow-violet-500/20",
        description: "Research-driven product design — flows, wireframes, design systems and interfaces that feel inevitable.",
        features: ["UX research & flows", "Design systems", "Interactive prototypes"],
        testid: "service-card-ui-ux",
    },
    {
        icon: Smartphone,
        title: "Mobile Apps",
        accent: "from-blue-500 to-blue-700",
        glow: "shadow-blue-500/20",
        description: "Native-quality mobile application design and development — fast, fluid and built to scale with you.",
        features: ["iOS & Android", "Cross-platform builds", "App store launch"],
        testid: "service-card-mobile-apps",
    },
    {
        icon: Globe,
        title: "Websites",
        accent: "from-pink-500 to-rose-600",
        glow: "shadow-pink-500/20",
        description: "High-performance website design and development — brand-led sites engineered for speed and SEO.",
        features: ["Marketing sites", "E-commerce", "Performance & SEO"],
        testid: "service-card-websites",
    },
    {
        icon: MonitorSmartphone,
        title: "Web Applications",
        accent: "from-amber-500 to-orange-600",
        glow: "shadow-amber-500/20",
        description: "Responsive web application development — complex products made simple, secure and beautiful.",
        features: ["Dashboards & portals", "SaaS platforms", "API integrations"],
        testid: "service-card-web-apps",
    },
];

export const Services = () => {
    return (
        <section id="services" className="relative py-20 lg:py-32 bg-gradient-to-b from-transparent via-violet-50/50 to-transparent">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                    className="max-w-2xl"
                >
                    <div className="font-mono-brand text-xs tracking-[0.25em] uppercase font-semibold text-violet-600 mb-4" data-testid="services-eyebrow">
                        What we do
                    </div>
                    <h2 className="font-display font-extrabold tracking-tight text-slate-900 text-2xl sm:text-3xl lg:text-4xl leading-snug">
                        Four disciplines. <span className="gradient-text">One obsession.</span>
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-slate-600">
                        Every capability under one roof, so your product never loses its soul between teams.
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {SERVICES.map((service, i) => (
                        <motion.div
                            key={service.title}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: i * 0.1 }}
                        >
                            <div className="gradient-border-card h-full" data-testid={service.testid}>
                                <div className="h-full rounded-[1.2rem] bg-white/85 backdrop-blur-xl p-7 flex flex-col">
                                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${service.accent} flex items-center justify-center shadow-lg ${service.glow}`}>
                                        <service.icon size={22} className="text-white" />
                                    </div>
                                    <h3 className="font-display font-bold text-xl text-slate-900 mt-6">{service.title}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed mt-3 flex-1">{service.description}</p>
                                    <ul className="mt-5 space-y-2">
                                        {service.features.map((feature) => (
                                            <li key={feature} className="text-xs font-medium text-slate-500 flex items-center gap-2">
                                                <span className="h-1.5 w-1.5 rounded-full gradient-bg shrink-0" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mt-10 relative rounded-[1.75rem] overflow-hidden border border-white/60 shadow-2xl shadow-violet-500/15"
                    data-testid="services-visual"
                >
                    <img
                        src={IMAGES.services}
                        alt="Abstract gradient artwork representing Brain Brush's service craft"
                        className="w-full h-52 sm:h-64 object-cover"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/70 via-slate-950/30 to-transparent flex items-center">
                        <div className="px-8 sm:px-12 max-w-md">
                            <p className="font-display font-bold text-white text-xl sm:text-2xl leading-snug">
                                Design that converts. Code that scales.
                            </p>
                            <button
                                data-testid="services-cta-start"
                                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white border border-white/40 rounded-full px-5 py-2.5 hover:bg-white/10 transition-colors"
                            >
                                Let's build yours <ArrowUpRight size={15} />
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
